import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, Truck, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api, CATEGORY_LABEL, formatRp, imgUrl } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const Gallery = ({ images, name }) => {
  const [idx, setIdx] = useState(0);
  const list = images?.length ? images : [""];
  return (
    <div>
      <div className="aspect-square overflow-hidden rounded-xl border border-stone-200 bg-stone-100">
        <img src={imgUrl(list[idx])} alt={name} className="h-full w-full object-cover" data-testid="product-main-image" />
      </div>
      {list.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto">
          {list.map((src, i) => (
            <button key={i} data-testid={`product-thumb-${i}`} onClick={() => setIdx(i)}
              className={`h-16 w-16 shrink-0 overflow-hidden rounded-md border-2 md:h-20 md:w-20 ${i === idx ? "border-amber-700" : "border-transparent"}`}>
              <img src={imgUrl(src)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const VariantPicker = ({ variants, value, onChange }) => variants.map((v) => (
  <div key={v.name} className="mt-5">
    <p className="text-sm font-medium">{v.name}</p>
    <div className="mt-2 flex flex-wrap gap-2">
      {v.options.map((o) => (
        <button key={o} data-testid={`variant-${v.name}-${o}`.replace(/\s+/g, "-").toLowerCase()} onClick={() => onChange({ ...value, [v.name]: o })}
          className={`rounded-lg border px-3 py-2 text-sm ${value[v.name] === o ? "border-amber-700 bg-amber-50 text-amber-900" : "border-stone-200 hover:bg-stone-100"}`}>{o}</button>
      ))}
    </div>
  </div>
));

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  const [variant, setVariant] = useState({});
  const [qty, setQty] = useState(1);
  const { add, setOpen } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setVariant(Object.fromEntries((r.data.variants || []).filter((v) => v.options.length).map((v) => [v.name, v.options[0]])));
    }).catch(() => setError(true));
  }, [id]);

  if (error) return <p className="py-24 text-center text-stone-500">Produk tidak ditemukan. <Link to="/" className="text-amber-700 underline">Kembali ke katalog</Link></p>;
  if (!p) return <div className="mx-auto grid max-w-6xl gap-8 px-4 py-8 md:grid-cols-2"><Skeleton className="aspect-square" /><Skeleton className="h-80" /></div>;

  const addToCart = (buyNow) => {
    add(p, variant, qty);
    toast.success("Ditambahkan ke keranjang");
    if (buyNow) setOpen(true);
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">
      <Link to="/" className="mb-4 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-amber-700"><ChevronLeft className="h-4 w-4" /> Katalog</Link>
      <div className="grid gap-8 md:grid-cols-2 md:gap-12">
        <Gallery images={p.images} name={p.name} />
        <div>
          <span className="text-sm text-stone-500">{CATEGORY_LABEL[p.category]}</span>
          <h1 className="mt-1 text-3xl font-semibold leading-tight" data-testid="product-name">{p.name}</h1>
          <div className="mt-4 flex items-baseline gap-3">
            <span className="tnum text-2xl font-semibold text-amber-800" data-testid="product-price">{formatRp(p.final_price)}</span>
            {p.discount_percent > 0 && <><span className="tnum text-sm text-stone-400 line-through">{formatRp(p.price)}</span><span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">-{p.discount_percent}%</span></>}
          </div>
          <p className="mt-2 text-sm" data-testid="product-stock">{p.stock > 0 ? <span className="text-green-700">Stok tersedia: {p.stock}</span> : <span className="text-red-700">Stok habis</span>}</p>
          <VariantPicker variants={p.variants || []} value={variant} onChange={setVariant} />
          <div className="mt-5">
            <p className="text-sm font-medium">Jumlah</p>
            <div className="mt-2 flex w-fit items-center rounded-lg border border-stone-200">
              <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-stone-100"><Minus className="h-4 w-4" /></button>
              <span className="tnum w-10 text-center" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-increase" onClick={() => setQty(Math.min(p.stock, qty + 1))} className="p-3 hover:bg-stone-100"><Plus className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <Button data-testid="add-to-cart-button" variant="outline" className="h-11 flex-1" disabled={p.stock <= 0} onClick={() => addToCart(false)}>Masukkan Keranjang</Button>
            <Button data-testid="buy-now-button" className="h-11 flex-1" disabled={p.stock <= 0} onClick={() => addToCart(true)}>Beli Sekarang</Button>
          </div>
          <div className="mt-6 flex items-start gap-3 rounded-lg border border-stone-200 bg-white p-4 text-sm">
            <Truck className="mt-0.5 h-5 w-5 text-amber-700" />
            <p className="text-stone-600">Dikirim dari Kosambi, Tangerang. Ongkir dihitung otomatis saat checkout (berat {p.weight_grams} g).</p>
          </div>
          <div className="mt-8">
            <h2 className="text-lg font-semibold">Deskripsi</h2>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-stone-700" data-testid="product-description">{p.description}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
